import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { prepareLogoDataUrl } from './imageUtils';
import { formatDateEs } from './index';

/**
 * Helper function to calculate age from date of birth (returns number only)
 * @param {string} dateOfBirth - Date of birth in ISO format
 * @returns {number} Age in years
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return '';

  try {
    let birthDate;

    // Handle different date formats
    if (typeof dateOfBirth === 'string') {
      // Try ISO format first
      const iso = dateOfBirth.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) {
        birthDate = new Date(iso[1], parseInt(iso[2]) - 1, parseInt(iso[3]));
      } else {
        // Try DD-MM-YYYY or DD/MM/YYYY
        const dmy = dateOfBirth.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
        if (dmy) {
          birthDate = new Date(parseInt(dmy[3]), parseInt(dmy[2]) - 1, parseInt(dmy[1]));
        } else {
          birthDate = new Date(dateOfBirth);
        }
      }
    } else if (dateOfBirth instanceof Date) {
      birthDate = dateOfBirth;
    } else {
      return '';
    }

    if (isNaN(birthDate.getTime())) {
      return '';
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 0 ? age : '';
  } catch (error) {
    return '';
  }
};

/**
 * Generates HTML content for prescription printing
 * @param {Object} prescription - Prescription data
 * @param {Object} patient - Patient data
 * @param {Object} organization - Organization data
 * @param {Object} doctor - Doctor data (optional)
 * @param {string} logoUrl - URL of the logo image (optional)
 * @returns {string} HTML content for printing
 */
export const generatePrescriptionHTML = (prescription, patient, organization, doctor, logoUrl) => {
  const patientName = patient ? `${patient.firstName} ${patient.lastName}` : 'Paciente';
  const patientAge = patient?.dateOfBirth ? calculateAge(patient.dateOfBirth) : '';

  // Get clinic name and doctor info
  const clinicName = organization?.name || 'NOMBRE DE LA CLÍNICA';
  const clinicPhone = organization?.phoneNumber || null;
  const doctorName = doctor?.name || prescription.doctorName || 'NOMBRE DEL MÉDICO';
  const doctorLicense = doctor?.licenseId || 'XXXXXXXXX';
  const doctorInstitution = doctor?.institution;
  const doctorSpecialization = doctor?.specialization;
  const clinicEmail = organization?.email || null;
  const clinicAddress = organization?.address ?
    `${organization.address.street || ''}${organization.address.street && organization.address.city ? ', ' : ''}${organization.address.city || ''}${organization.address.city && organization.address.state ? ', ' : ''}${organization.address.state || ''}` :
    null;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Prescripción Médica - ${patientName}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: Arial, sans-serif;
          margin: 20px;
          line-height: 1.4;
          color: #000;
          background: white;
        }
        
        .prescription-header {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid #000;
          position: relative;
          min-height: 140px;
        }
        
        .folio-number {
          position: absolute;
          top: 0;
          right: 0;
          font-size: 14px;
          font-weight: bold;
          border: 2px solid #000;
          padding: 8px 12px;
          background: white;
          z-index: 10;
        }
        
        .logo-section {
          position: absolute;
          top: 0;
          left: 0;
          width: 240px;
          height: 100px;
          display: flex;
          align-items: flex-start;
          justify-content: flex-start;
          opacity: 0.8;
          z-index: 0;
        }

        .logo-section img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          object-position: left top;
        }
        
        .header-content {
          position: relative;
          width: 100%;
          text-align: center;
          z-index: 1;
        }
        
        .clinic-name {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 15px;
          text-transform: uppercase;
        }
        
        .doctor-info {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 10px;
          gap: 40px;
        }
        
        .titulo {
          font-size: 16px;
          font-weight: bold;
        }
        
        .doctor-name {
          font-size: 16px;
          font-weight: bold;
          text-align: center;
        }
        
        .license-info {
          font-size: 14px;
        }
        
        .school-name {
          font-size: 14px;
          font-weight: bold;
          text-transform: uppercase;
        }
        
        .patient-section {
          margin: 30px 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 14px;
        }
        
        .patient-line {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .field-label {
          font-weight: normal;
        }
        
        .field-value {
          border-bottom: 1px solid #000;
          min-width: 200px;
          padding-bottom: 2px;
          text-align: center;
        }
        
        .small-field {
          min-width: 80px;
        }
        
        .prescription-content {
          margin: 40px 0;
          min-height: 300px;
        }
        
        .rx-title {
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 30px;
        }
        
        .medication-list {
          margin: 20px 0;
          padding-left: 20px;
        }
        
        .medication-item {
          margin: 15px 0;
          line-height: 1.6;
          font-size: 14px;
        }
        
        .medication-name {
          font-weight: bold;
          font-size: 16px;
          margin-bottom: 5px;
        }
        
        .medication-instructions {
          margin-left: 20px;
          color: #333;
        }
        
        .footer-section {
          margin-top: 60px;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        
        .contact-info {
          font-size: 12px;
          line-height: 1.4;
        }
        
        .signature-area {
          text-align: center;
          width: 300px;
        }
        
        .signature-line {
          border-top: 1px solid #000;
          margin: 40px 0 10px 0;
        }
        
        .doctor-signature {
          font-size: 14px;
          line-height: 1.4;
        }
        
        .license-number {
          font-size: 12px;
          margin-top: 5px;
        }
        
        .doctor-specialization {
          font-size: 12px;
          margin-top: 3px;
          font-style: italic;
          color: #555;
        }
        
        .watermark {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-45deg);
          font-size: 120px;
          font-weight: bold;
          color: rgba(255, 0, 0, 0.15);
          text-transform: uppercase;
          z-index: 1000;
          pointer-events: none;
          white-space: nowrap;
          letter-spacing: 10px;
        }
        
        .notes-section {
          margin: 30px 0;
          padding: 15px;
          background: #f9f9f9;
          border-left: 4px solid #666;
        }
        
        .notes-title {
          font-weight: bold;
          margin-bottom: 10px;
        }
        
        @media print {
          body { margin: 15px; }
          .prescription-header { break-inside: avoid; }
          .medication-item { break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      ${prescription.status === 'cancelled' ? '<div class="watermark">CANCELADA</div>' : ''}
      <div class="prescription-header">
        <div class="folio-number">
          Folio: ${prescription.folioNumber || 'N/A'}
        </div>
        
        ${logoUrl ? `
        <div class="logo-section">
          <img src="${logoUrl}" alt="Logo Clínica">
        </div>
        ` : ''}

        <div class="header-content">
          <div class="clinic-name">${clinicName}</div>
          
          <div class="doctor-info">
            <div class="doctor-name">${doctorName}</div>
          </div>
          
          <div class="license-info">
            ${doctorSpecialization ? `${doctorSpecialization} - ` : ''}Cédula Profesional: ${doctorLicense}
          </div>

          ${doctorInstitution ? `
            <div class="school-name">
              ${doctorInstitution}
            </div>
          ` : ''}
        </div>
      </div>

      <div class="patient-section">
        <div class="patient-line">
          <span class="field-label">Nombre del paciente:</span>
          <span class="field-value">${patientName}</span>
        </div>
        
        <div class="patient-line">
          <span class="field-label">Edad:</span>
          <span class="field-value small-field">${patientAge}</span>
        </div>
        
        <div class="patient-line">
          <span class="field-label">Fecha:</span>
          <span class="field-value small-field">${formatDateEs(prescription.date)}</span>
        </div>
      </div>

      <div class="prescription-content">
        <div class="medication-list">
          ${prescription.medications?.map((medication, index) => `
            <div class="medication-item">
              <div class="medication-name">
                ${index + 1}. ${medication.farmaco?.commercialName || medication.farmaco?.components} ${medication.farmaco?.components ? `(${medication.farmaco?.components})` : ''}
                ${medication.farmaco?.dosis ? `- ${medication.farmaco?.dosis}` : ''}
                ${medication.farmaco?.administrationForm ? ` - ${medication.farmaco?.administrationForm}` : ''}
              </div>
              <div class="medication-instructions">
                ${medication.periodicity} por ${medication.duration}
                ${medication.notes ? `<br><em>${medication.notes}</em>` : ''}
              </div>
            </div>
          `).join('') || '<div class="medication-item">No se han prescrito medicamentos</div>'}
        </div>

        ${prescription.generalNotes ? `
          <div class="notes-section">
            <div class="notes-title">Notas Generales:</div>
            <div>${prescription.generalNotes}</div>
          </div>
        ` : ''}
      </div>

      <div class="footer-section">
        <div class="contact-info">
          ${clinicEmail ? `<div><strong>Correo:</strong> ${clinicEmail}</div>` : ''}
          ${clinicAddress ? `<div><strong>Dirección:</strong> ${clinicAddress}</div>` : ''}
          ${clinicPhone ? `<div><strong>Teléfono:</strong> ${clinicPhone}</div>` : ''}
        </div>
        
        <div class="signature-area">
          <div class="signature-line"></div>
          <div class="doctor-signature">
            <strong>${doctorName}</strong><br>
            <div class="license-number">Cédula profesional: ${doctorLicense}</div>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Prints a prescription by opening a new window with the prescription content
 * @param {Object} prescription - Prescription data
 * @param {Object} patient - Patient data
 * @param {Object} organization - Organization data (optional)
 * @param {Object} doctor - Doctor data (optional)
 * @param {string} logoUrl - URL of the logo image (optional)
 */
export const printPrescription = async (prescription, patient, organization, doctor, logoUrl) => {
  try {
    // Open the window synchronously (popup blockers require a user gesture),
    // then prepare the logo and write the content.
    const printWindow = window.open('', '_blank');

    const inlineLogoUrl = await prepareLogoDataUrl(logoUrl);
    const printContent = generatePrescriptionHTML(prescription, patient, organization, doctor, inlineLogoUrl);

    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for the content to load, then trigger print
    printWindow.onload = () => {
      printWindow.print();
    };
  } catch (error) {
    console.error('Error printing prescription:', error);
    throw new Error('Error al imprimir la prescripción');
  }
};

/**
 * Builds a filename for the downloaded prescription. Folio first so the files
 * sort the way the clinic numbers them; falls back to the date for the rare
 * prescription that has no folio yet.
 * @param {Object} prescription - Prescription data
 * @param {Object} patient - Patient data
 * @returns {string} File name ending in .pdf
 */
const buildPrescriptionFileName = (prescription, patient) => {
  const patientSlug = `${patient?.firstName || ''}_${patient?.lastName || ''}`
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^\w\-áéíóúÁÉÍÓÚñÑ]/g, '') || 'paciente';

  const reference = prescription?.folioNumber
    ? `folio_${prescription.folioNumber}`
    : new Date().toISOString().split('T')[0];

  return `receta_${patientSlug}_${reference}.pdf`;
};

/**
 * Renders a prescription to a PDF Blob.
 *
 * Reuses generatePrescriptionHTML so the downloaded file is byte-for-byte the
 * same layout as the printed one — including the CANCELADA watermark. The HTML
 * is rendered in an offscreen iframe rather than the live document so page
 * styles cannot bleed into it, matching how treatment plans are exported.
 *
 * @param {Object} prescription - Prescription data
 * @param {Object} patient - Patient data
 * @param {Object} organization - Organization data (optional)
 * @param {Object} doctor - Doctor data (optional)
 * @param {string} logoUrl - URL of the logo image (optional)
 * @returns {Promise<Blob>} The generated PDF
 */
export const generatePrescriptionPdfBlob = async (prescription, patient, organization, doctor, logoUrl) => {
  let iframe = null;

  const waitForImages = async (doc) => {
    const images = Array.from(doc.images || []);
    if (images.length === 0) return;
    await Promise.all(images.map((img) => new Promise((resolve) => {
      if (img.complete) {
        resolve();
        return;
      }
      img.onload = resolve;
      img.onerror = resolve;
    })));
  };

  try {
    // html2canvas drops cross-origin images unless the bucket sends CORS
    // headers, so inline the logo as a whitespace-trimmed data URL before
    // rendering.
    const inlineLogoUrl = await prepareLogoDataUrl(logoUrl);
    const content = generatePrescriptionHTML(prescription, patient, organization, doctor, inlineLogoUrl);

    iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.top = '0';
    iframe.style.width = '900px';
    iframe.style.height = '1200px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument;
    if (!iframeDoc) {
      throw new Error('No se pudo inicializar el documento del PDF.');
    }

    iframeDoc.open();
    iframeDoc.write(content);
    iframeDoc.close();

    await new Promise((resolve) => setTimeout(resolve, 50));
    await waitForImages(iframeDoc);

    const canvas = await html2canvas(iframeDoc.body, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: iframeDoc.documentElement.scrollWidth,
      windowHeight: iframeDoc.documentElement.scrollHeight,
      scrollX: 0,
      scrollY: 0
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    return pdf.output('blob');
  } catch (error) {
    console.error('Error generating prescription PDF:', error);
    throw new Error('Error al generar el PDF de la prescripción');
  } finally {
    if (iframe && iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }
};

/**
 * Generates the prescription PDF and hands it to the browser as a download.
 * @param {Object} prescription - Prescription data
 * @param {Object} patient - Patient data
 * @param {Object} organization - Organization data (optional)
 * @param {Object} doctor - Doctor data (optional)
 * @param {string} logoUrl - URL of the logo image (optional)
 */
export const downloadPrescriptionPDF = async (prescription, patient, organization, doctor, logoUrl) => {
  const blob = await generatePrescriptionPdfBlob(prescription, patient, organization, doctor, logoUrl);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = buildPrescriptionFileName(prescription, patient);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
};
